import {
  Button,
  Card,
  Col,
  Divider,
  Form,
  Input,
  Radio,
  Row,
  Select,
  Space,
  Switch,
  Typography,
} from 'antd';
import React, { useState } from 'react';

import { useContentProfiles } from '../hooks/useContentProfiles';
import { useCreateNewCustomer } from '../hooks/useCreateNewCustomer';
import { maskUtils } from '../utils/masks';

const { Title } = Typography;

export const CreateNewCustomerScreen: React.FC = () => {
  const [form] = Form.useForm();
  const [sendPasswordEmail, setSendPasswordEmail] = useState(false);

  const { createNewCustomer, isPending } = useCreateNewCustomer();
  const { contentProfiles, contentProfilesLoading } = useContentProfiles();

  const handleSubmit = async (values: any) => {
    const diasAcesso = values.customDays ?? values.selectedDays;
    const payload = {
      name: values.name,
      email: values.email,
      document: values.document
        ? maskUtils.removeMask(values.document)
        : undefined,
      phone: values.phone ? maskUtils.removeMask(values.phone) : undefined,
      accessDays: Number(diasAcesso),
      sendPasswordEmail: values.sendPasswordEmail,
      password: values.sendPasswordEmail ? undefined : values.password,
      profilesIds: values.profilesIds?.map(Number) || [],
      segmentationId: values.segmentationId,
    };

    await createNewCustomer(payload);
    form.resetFields();
    setSendPasswordEmail(false);
  };

  return (
    <Row justify='center' style={{ padding: '32px 8px' }}>
      <Col span={18}>
        <Card
          title={<Title level={4}>Cadastrar Novo Usuário</Title>}
          bordered={false}
          style={{
            borderRadius: '16px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          }}
        >
          <Form
            layout='vertical'
            form={form}
            onFinish={handleSubmit}
            initialValues={{
              selectedDays: 365,
              sendPasswordEmail: false,
            }}
          >
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label='Nome'
                  name='name'
                  rules={[{ required: true, message: 'Informe o nome' }]}
                >
                  <Input placeholder='Digite o nome completo' />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label='Email'
                  name='email'
                  rules={[
                    { required: true, message: 'Informe o e-mail' },
                    { type: 'email', message: 'E-mail inválido' },
                  ]}
                >
                  <Input placeholder='usuario@exemplo.com' />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={12}>
                <Form.Item label='Documento' name='document'>
                  <Input
                    placeholder='CPF ou RG (opcional)'
                    maxLength={14}
                    onChange={e => {
                      form.setFieldsValue({
                        document: maskUtils.formatCPF(e.target.value),
                      });
                    }}
                  />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item label='Telefone' name='phone'>
                  <Input
                    placeholder='(00) 00000-0000'
                    maxLength={19}
                    onChange={e => {
                      form.setFieldsValue({
                        phone: maskUtils.formatPhone(e.target.value),
                      });
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col xxl={12} sm={24}>
                <Form.Item label='Dias de Acesso' required>
                  <Space align='start' wrap>
                    <Form.Item
                      name='selectedDays'
                      rules={[
                        {
                          validator: (_, value) => {
                            const custom = form.getFieldValue('customDays');
                            if (!value && !custom) {
                              return Promise.reject(
                                'Informe os dias de acesso'
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      noStyle
                    >
                      <Radio.Group
                        onChange={() => {
                          form.setFieldsValue({ customDays: undefined });
                        }}
                      >
                        <Space size='small' wrap>
                          <Radio.Button value={15}>15</Radio.Button>
                          <Radio.Button value={30}>30</Radio.Button>
                          <Radio.Button value={60}>60</Radio.Button>
                          <Radio.Button value={90}>90</Radio.Button>
                          <Radio.Button value={120}>120</Radio.Button>
                          <Radio.Button value={180}>180</Radio.Button>
                          <Radio.Button value={250}>250</Radio.Button>
                          <Radio.Button value={300}>300</Radio.Button>
                          <Radio.Button value={365}>365</Radio.Button>
                        </Space>
                      </Radio.Group>
                    </Form.Item>

                    <Form.Item
                      name='customDays'
                      rules={[
                        {
                          validator: (_, value) => {
                            const selected = form.getFieldValue('selectedDays');
                            if (!selected && !value) {
                              return Promise.reject(
                                'Informe os dias de acesso'
                              );
                            }
                            if (value && (isNaN(value) || value < 1)) {
                              return Promise.reject(
                                'Valor deve ser maior ou igual a 1'
                              );
                            }
                            return Promise.resolve();
                          },
                        },
                      ]}
                      noStyle
                    >
                      <Input
                        placeholder='Outro valor'
                        type='number'
                        style={{ width: 96, textAlign: 'center' }}
                        onChange={e => {
                          form.setFieldsValue({
                            selectedDays: undefined,
                            customDays: e.target.value,
                          });
                        }}
                      />
                    </Form.Item>
                  </Space>
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label='Segmentação'
                  name='segmentationId'
                  rules={[
                    { required: true, message: 'Segmentação é obrigatória' },
                  ]}
                >
                  <Select placeholder='Selecione a segmentação'>
                    <Select.Option value={1}>Brasil</Select.Option>
                    <Select.Option value={2}>LATAM</Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label='Enviar e-mail de criação de senha'
                  name='sendPasswordEmail'
                  valuePropName='checked'
                >
                  <Switch
                    onChange={(checked) => {
                      setSendPasswordEmail(checked);
                      if (checked) {
                        form.setFieldsValue({
                          password: undefined,
                          confirmPassword: undefined,
                        });
                      }
                    }}
                  />
                </Form.Item>
              </Col>
            </Row>

            {!sendPasswordEmail && (
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label='Senha'
                    name='password'
                    rules={[
                      { required: true, message: 'Senha é obrigatória' },
                      { min: 6, message: 'Senha deve ter no mínimo 6 caracteres' },
                    ]}
                  >
                    <Input.Password placeholder='Digite a senha' />
                  </Form.Item>
                </Col>

                <Col span={12}>
                  <Form.Item
                    label='Confirmar Senha'
                    name='confirmPassword'
                    dependencies={['password']}
                    rules={[
                      { required: true, message: 'Confirmação de senha é obrigatória' },
                      ({ getFieldValue }) => ({
                        validator(_, value) {
                          if (!value || getFieldValue('password') === value) {
                            return Promise.resolve();
                          }
                          return Promise.reject(new Error('As senhas não coincidem'));
                        },
                      }),
                    ]}
                  >
                    <Input.Password placeholder='Confirme a senha' />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row gutter={16}>
              <Col span={24}>
                <Form.Item
                  label='Perfis'
                  name='profilesIds'
                >
                  <Select
                    mode='multiple'
                    placeholder='Selecione os perfis'
                    loading={contentProfilesLoading}
                    optionFilterProp='children'
                    showSearch
                    allowClear
                  >
                    {contentProfiles?.map(profile => (
                      <Select.Option key={profile.id} value={profile.id}>
                        {profile.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Divider />

            <Space style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={() => {
                form.resetFields();
                setSendPasswordEmail(false);
              }}>
                Cancelar
              </Button>
              <Button type='primary' htmlType='submit' loading={isPending}>
                Salvar Usuário
              </Button>
            </Space>
          </Form>
        </Card>
      </Col>
    </Row>
  );
};